import React, { useEffect, useState } from "react";
import { ConfigProvider, DatePicker } from "antd";
import dayjs from "dayjs";
import { ManageCategory, ManageRestaurant } from "../../Functionalities/Functions";
import { ManageFranchise } from "../Franchise/Logic";
import { sees_all_franchises } from "../../Functionalities/Config";
import { AMOUNT_LABELS, fmtDate } from "./format";
import { isDirty, matchingPresetDays, presetRange } from "./filterState";

const { RangePicker } = DatePicker;

const listOf = (result) => {
  if (!result || !result.data) return [];
  const rows = Array.isArray(result.data) ? result.data : result.data.results;
  return Array.isArray(rows) ? rows : [];
};

const PRESETS = [
  ["Yesterday", 1],
  ["7 days", 7],
  ["30 days", 30],
  ["90 days", 90],
];

const FULFILMENTS = [
  { id: "DELIVERY", name: "Delivery" },
  { id: "SELF_PICKUP", name: "Self pickup" },
  { id: "EXPRESS", name: "Express" },
  { id: "SCHEDULED", name: "Scheduled" },
];

const PAYMENT_MODES = [
  { id: "COD", name: "Cash on delivery" },
  { id: "ONLINE", name: "Online" },
  { id: "OTHER", name: "Wallet / other" },
];

const SORTS = [
  { id: "orders", name: "Rank by orders" },
  { id: "revenue", name: "Rank by revenue" },
];

const AMOUNTS = Object.keys(AMOUNT_LABELS).map((id) => ({ id, name: AMOUNT_LABELS[id] }));

const THEME = {
  token: {
    colorPrimary: "#fb310d",
    borderRadius: 8,
    fontFamily: '"Inter", sans-serif',
    controlHeight: 36,
  },
};

export default function FilterBar({ filters, range, dataFrom, dataThrough, onChange, onRefresh, loading }) {
  const [branches, setBranches] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pending, setPending] = useState(filters);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!loading) setIsSubmitting(false);
  }, [loading]);

  useEffect(() => {
    ManageRestaurant("", "", "get", "page_wise=false&for_dropdown=1&order_by_name=true").then((r) => setBranches(listOf(r)));
    ManageCategory("", "", "get", "page_wise=false").then((r) => setCategories(listOf(r)));
    if (sees_all_franchises) ManageFranchise("", "", "get", "page_wise=false&for_dropdown=1").then((r) => setFranchises(listOf(r)));
  }, []);

  useEffect(() => {
    setPending(filters);
  }, [filters]);

  const edit = (patch) => setPending((prev) => ({ ...prev, ...patch }));
  const applyPreset = (days) => {
    const next = presetRange(dataThrough, days);
    if (next) onChange({ ...pending, ...next });
  };
  const fetchNow = () => {
    setIsSubmitting(true);
    onChange(pending);
  };
  const isLoading = Boolean(loading || isSubmitting);

  const from = pending.from || (range && range.from) || "";
  const to = pending.to || (range && range.to) || "";
  const activeDays = matchingPresetDays(from, to, dataThrough);
  const dirty = isDirty(pending, filters);

  const select = (name, options, defaultLabel) => (
    <select
      className="ins-dropdown-select"
      value={pending[name] || ""}
      onChange={(e) => edit({ [name]: e.target.value })}
      aria-label={defaultLabel}
    >
      <option value="">{defaultLabel}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  );

  const outsideData = (day) =>
    (dataThrough && day.isAfter(dayjs(dataThrough), "day")) || (dataFrom && day.isBefore(dayjs(dataFrom), "day"));

  const renderRefreshBtn = (className = "") => (
    <button
      type="button"
      className={`ins-btn-action ${className}`}
      onClick={onRefresh || fetchNow}
      title="Refresh analytics data"
      disabled={isLoading}
    >
      <span className="material-symbols-outlined" style={{ fontSize: "16px" }}>refresh</span>
      <span>Refresh</span>
    </button>
  );

  const renderFetchBtn = (className = "") => (
    <button
      type="button"
      className={`ins-btn-action primary ${dirty && !isLoading ? "dirty" : ""} ${className}`}
      onClick={fetchNow}
      disabled={(!dirty && !isLoading) || isLoading}
      title={dirty ? "Apply pending filter changes" : "Filters are up to date"}
    >
      {isLoading ? (
        <>
          <span className="ins-btn-spinner" />
          <span>Fetching…</span>
        </>
      ) : (
        <>
          <span className="material-symbols-outlined" style={{ fontSize: "16px" }}>filter_alt</span>
          <span>Fetch data</span>
        </>
      )}
    </button>
  );

  return (
    <section className="ins-filter-bar ins-section">
      <div className="ins-filter-rows">
        <div className="ins-filter-row">
          <div className="ins-filter-controls">
            <div className="ins-preset-group" role="group" aria-label="Time Presets">
              {PRESETS.map(([label, days]) => (
                <button
                  type="button"
                  key={label}
                  className={`ins-preset-btn ${activeDays === days ? "active" : ""}`}
                  onClick={() => applyPreset(days)}
                  disabled={!dataThrough}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="ins-range-wrapper">
              <ConfigProvider theme={THEME}>
                <RangePicker
                  value={[from ? dayjs(from) : null, to ? dayjs(to) : null]}
                  onChange={(dates) =>
                    edit({
                      from: dates && dates[0] ? dates[0].format("YYYY-MM-DD") : "",
                      to: dates && dates[1] ? dates[1].format("YYYY-MM-DD") : "",
                    })
                  }
                  disabledDate={outsideData}
                  format="DD/MM/YYYY"
                  allowClear={false}
                  allowEmpty={[true, true]}
                  disabled={!dataThrough}
                  aria-label="Date range"
                />
              </ConfigProvider>
              <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 2 }}>
                Days run 4 AM to 4 AM, by the day orders were placed
              </div>
            </div>

            {sees_all_franchises && select("franchise", franchises, "All franchises")}

            {select(
              "branch",
              branches,
              branches.length > 0 ? `All stores (${branches.length} locations)` : "All stores"
            )}

            {select("fulfilment", FULFILMENTS, "All order types")}
          </div>

          <div className="ins-btn-desktop-wrap">
            {renderRefreshBtn()}
          </div>
        </div>

        <div className="ins-filter-row">
          <div className="ins-filter-controls">
            {select("payment_mode", PAYMENT_MODES, "All payments")}

            {select("category", categories, "All categories")}

            {select("sort", SORTS, "Rank by orders")}

            <select
              className="ins-dropdown-select"
              value={pending.amount || "food"}
              onChange={(e) => edit({ amount: e.target.value })}
              aria-label="Amount basis"
            >
              {AMOUNTS.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>

          <div className="ins-btn-desktop-wrap">
            {renderFetchBtn()}
          </div>
        </div>

        <div className="ins-filter-actions-mobile">
          {renderRefreshBtn("ins-btn-mobile")}
          {renderFetchBtn("ins-btn-mobile")}
        </div>
      </div>

      <div className="ins-sync-line">
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span className="ins-pulse-dot" />
          <span>
            Numbers run through <strong>{dataThrough ? fmtDate(dataThrough) : "recent data"}</strong>, the last closed day.
            A business day runs <strong>4 AM to 4 AM IST</strong>, so late-night orders count toward the evening they belong to.
          </span>
        </div>
        <div style={{ color: "#94a3b8", fontSize: "0.75rem", fontWeight: 500 }}>
          {dataFrom && dataThrough ? `Coverage: ${fmtDate(dataFrom)} → ${fmtDate(dataThrough)}` : "Live Ops Enabled"}
        </div>
      </div>
    </section>
  );
}
